import { useEffect, useState } from "react";

import {
  useRecordingConfig,
  useUpsertRecordingConfig,
} from "@/api/recordings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RETENTION_OPTIONS, type RecordingMode } from "@/types";


interface RecordingConfigDrawerProps {
  cameraId: string | null;
  cameraName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MODE_OPTIONS: Array<{
  value: RecordingMode;
  label: string;
  disabled?: boolean;
  hint?: string;
}> = [
  { value: "continuous", label: "Continuous" },
  { value: "schedule", label: "Schedule" },
  {
    value: "motion",
    label: "Motion",
    disabled: true,
    hint: "Coming soon",
  },
];


export function RecordingConfigDrawer({
  cameraId,
  cameraName,
  open,
  onOpenChange,
}: RecordingConfigDrawerProps) {
  const { data: cfg } = useRecordingConfig(open ? cameraId ?? undefined : undefined);
  const upsert = useUpsertRecordingConfig(cameraId ?? "");

  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState<RecordingMode>("continuous");
  const [retention, setRetention] = useState(10);
  const [scheduleStart, setScheduleStart] = useState("09:00");
  const [scheduleEnd, setScheduleEnd] = useState("17:00");

  // Pull current values when the drawer opens (or the cached value changes).
  useEffect(() => {
    if (!open) return;
    if (cfg) {
      setEnabled(cfg.enabled);
      setMode(cfg.mode);
      setRetention(cfg.retention_minutes);
      if (cfg.schedule_start) setScheduleStart(cfg.schedule_start.slice(0, 5));
      if (cfg.schedule_end) setScheduleEnd(cfg.schedule_end.slice(0, 5));
    } else {
      setEnabled(true);
      setMode("continuous");
      setRetention(10);
    }
  }, [open, cfg]);

  if (!cameraId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Recording config{cameraName ? ` — ${cameraName}` : ""}</DialogTitle>
          <DialogDescription>
            Per-camera continuous recording to S3. Segments expire after
            the retention window.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm font-medium">Enable recording</span>
          </label>

          <div className="space-y-1">
            <Label>Mode</Label>
            <div className="flex flex-wrap gap-2">
              {MODE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={opt.disabled}
                  onClick={() => setMode(opt.value)}
                  className={`rounded border px-3 py-1.5 text-xs transition ${
                    mode === opt.value
                      ? "border-[#ff6d00] bg-[#ff6d00]/10 text-foreground"
                      : "border-border text-muted-foreground hover:bg-muted"
                  } ${opt.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                  title={opt.hint}
                >
                  {opt.label}
                  {opt.hint && (
                    <span className="ml-1 text-[10px] uppercase tracking-wide">
                      ({opt.hint})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="retention">Retention</Label>
            <select
              id="retention"
              value={retention}
              onChange={(e) => setRetention(Number(e.target.value))}
              className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
            >
              {RETENTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {mode === "schedule" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="sch_start">Start</Label>
                <Input
                  id="sch_start"
                  type="time"
                  value={scheduleStart}
                  onChange={(e) => setScheduleStart(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sch_end">End</Label>
                <Input
                  id="sch_end"
                  type="time"
                  value={scheduleEnd}
                  onChange={(e) => setScheduleEnd(e.target.value)}
                />
              </div>
            </div>
          )}

          {upsert.isError && (
            <p className="text-xs text-destructive">
              Save failed: {(upsert.error as Error).message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={upsert.isPending}
            onClick={async () => {
              try {
                await upsert.mutateAsync({
                  enabled,
                  mode,
                  retention_minutes: retention,
                  schedule_start: mode === "schedule" ? `${scheduleStart}:00` : null,
                  schedule_end: mode === "schedule" ? `${scheduleEnd}:00` : null,
                });
                onOpenChange(false);
              } catch {
                // toast handled via upsert.isError above
              }
            }}
          >
            {upsert.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
